import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import './EventsDetallePage.css';
import { logError, warn } from '../../utils/logger';

const resolveImageUrl = (source) => {
  if (!source) return null;
  const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const base = getApiBaseUrl();
  if (sanitized.startsWith('/')) return `${base}${sanitized}`;
  if (sanitized.startsWith('uploads/')) return `${base}/${sanitized}`;
  return `${base}/uploads/${sanitized}`;
};

const EventsDetallePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user, token } = useAuth();
  const { notify } = useNotifications();
  const [event, setEvent] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingToCart, setAddingToCart] = useState(false);
  const [expandedDay, setExpandedDay] = useState(null);

  useEffect(() => {
    fetchEventData();
  }, [id]);

  const fetchEventData = async () => {
    try {
      setLoading(true);
      
      // Obtener el producto del evento con toda la información (incluye días con clases)
      const productResponse = await fetch(buildApiUrl(`/api/products/public/event/${id}`));
      if (!productResponse.ok) {
        throw new Error('Evento no encontrado');
      }
      const productData = await productResponse.json();
      
      setProduct(productData.product);
      setEvent(productData.product?.event);
    } catch (err) {
      logError('[EventsDetallePage] Error fetching event:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateFinalPrice = (price, discountPercent) => {
    const numPrice = parseFloat(price || 0);
    const numDiscount = parseFloat(discountPercent || 0);
    if (!numDiscount || numDiscount === 0) return numPrice;
    return numPrice * (1 - numDiscount / 100);
  };

  const handleAddToCart = async () => {
    if (!product) {
      warn('No hay producto disponible para agregar al carrito');
      return;
    }
    
    if (!user || !token) {
      notify({
        message: 'Debes iniciar sesión para agregar eventos al carrito',
        type: 'info',
      });
      navigate('/login', { state: { from: `/events/${id}` } });
      return;
    }
    
    const eventImage = event?.imagen || event?.imagenevento || null;
    const imageUrl = eventImage ? resolveImageUrl(eventImage) : null;
    
    setAddingToCart(true);
    const success = await addToCart(
      {
        id: product.id,
        name: event?.nombre || event?.tituloevento || 'Evento sin nombre',
        price: parseFloat(product.price || 0),
        image: imageUrl || '',
        type: 'event',
        quantity: 1,
      },
      token,
      (errorMessage) => {
        notify({
          message: errorMessage,
          type: 'warning',
        });
      }
    );
    
    if (success) {
      notify({
        message: 'El evento se agregó al carrito exitosamente.',
        type: 'success',
      });
    }
    setAddingToCart(false);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(price);
  };

  const formatTime = (time) => {
    if (!time) return '';
    if (typeof time === 'string') {
      const parts = time.split(':');
      return `${parts[0]}:${parts[1]}`;
    }
    return time;
  };

  const formatEventDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-AR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="event-detail-loading">
        <div className="event-detail-spinner"></div>
        <p>Cargando evento...</p>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="event-detail-error">
        <h2>Evento no encontrado</h2>
        <p>{error || 'El evento que buscas no existe o no está disponible.'}</p>
        <button onClick={() => navigate('/events')} className="event-detail-back-btn">
          Volver a eventos
        </button>
      </div>
    );
  }

  const eventPrice = parseFloat(product?.price || 0);
  const discountPercent = parseFloat(product?.discountPercent || 0);
  const finalPrice = calculateFinalPrice(eventPrice, discountPercent);
  const hasDiscount = discountPercent > 0;
  const isFree = finalPrice === 0 || eventPrice === 0;
  const eventImage = resolveImageUrl(event.imagen || event.imagenevento);
  const diaseventos = event.dias || event.diaseventos || [];
  const hasAvailableSlots = (event.cupos || event.cuposdisponibles || 0) > 0;

  return (
    <div className="event-detail-page">
      {/* Hero Section */}
      <section className="event-detail-hero">
        <div className="event-detail-hero-container">
          <div className="event-detail-hero-content">
            <div className="event-detail-image-wrapper">
              {eventImage ? (
                <img 
                  src={eventImage} 
                  alt={event.nombre || 'Imagen del evento'} 
                  className="event-detail-image"
                />
              ) : (
                <div className="event-detail-image-placeholder">
                  <i className="fas fa-calendar-alt"></i>
                </div>
              )}
            </div>
            
            <div className="event-detail-info">
              <div className="event-detail-header">
                <h1 className="event-detail-title">{event.nombre || event.tituloevento || 'Evento sin nombre'}</h1>
                {event.formato && (
                  <span className={`event-format-badge-detail ${event.formato === 'virtual' ? 'virtual' : 'presencial'}`}>
                    <i className={`fas fa-${event.formato === 'virtual' ? 'video' : 'map-marker-alt'}`}></i>
                    {event.formato === 'virtual' ? 'Virtual' : 'Presencial'}
                  </span>
                )}
              </div>
              
              {event.descripcionBreve && (
                <p className="event-detail-subtitle">{event.descripcionBreve}</p>
              )}

              <div className="event-detail-price-section">
                {hasDiscount ? (
                  <>
                    <div className="event-detail-price-original">
                      {formatPrice(eventPrice)}
                    </div>
                    <div className="event-detail-price-final">
                      {formatPrice(finalPrice)}
                    </div>
                    <div className="event-detail-discount-badge">
                      -{discountPercent}% OFF
                    </div>
                  </>
                ) : (
                  <div className="event-detail-price-final">
                    {isFree ? 'Gratis' : formatPrice(eventPrice)}
                  </div>
                )}
              </div>

              <div className="event-detail-meta">
                {event.cupos !== undefined && (
                  <div className="event-detail-meta-item">
                    <i className="fas fa-users"></i>
                    <span>
                      {event.cupos || event.cuposdisponibles || 0} cupos disponibles
                    </span>
                  </div>
                )}
                {diaseventos.length > 0 && (
                  <div className="event-detail-meta-item">
                    <i className="fas fa-calendar-check"></i>
                    <span>{diaseventos.length} {diaseventos.length === 1 ? 'día' : 'días'}</span>
                  </div>
                )}
              </div>

              <button 
                className="event-detail-cart-btn"
                onClick={handleAddToCart}
                disabled={addingToCart || !hasAvailableSlots}
              >
                {addingToCart ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Agregando...
                  </>
                ) : !hasAvailableSlots ? (
                  <>
                    <i className="fas fa-times-circle"></i>
                    Sin cupos disponibles
                  </>
                ) : (
                  <>
                    <i className="fas fa-shopping-cart"></i>
                    Agregar al carrito
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Description Section */}
      {event.descripcionExtendida && (
        <section className="event-detail-description">
          <div className="event-detail-container">
            <h2 className="event-detail-section-title">Descripción</h2>
            <div 
              className="event-detail-description-content"
              dangerouslySetInnerHTML={{ __html: event.descripcionExtendida }}
            />
          </div>
        </section>
      )}

      {/* Event Days Section */}
      {diaseventos.length > 0 && (
        <section className="event-detail-days">
          <div className="event-detail-container">
            <h2 className="event-detail-section-title">
              <i className="fas fa-calendar-alt me-2"></i>
              Días del Evento
            </h2>
            <div className="event-detail-days-list">
              {diaseventos.map((dia, index) => {
                const dayId = dia.id || index;
                const isExpanded = expandedDay === dayId;
                const clases = dia.clases || [];
                const hasClasses = clases.length > 0;
                
                return (
                  <div 
                    key={dayId} 
                    className={`event-day-card ${isExpanded ? 'expanded' : ''} ${hasClasses ? 'has-classes' : ''}`}
                    onClick={() => hasClasses && setExpandedDay(isExpanded ? null : dayId)}
                    style={{ cursor: hasClasses ? 'pointer' : 'default' }}
                  >
                    <div className="event-day-header">
                      <div className="event-day-date">
                        <i className="fas fa-calendar"></i>
                        <span>{formatEventDate(dia.fecha)}</span>
                        {hasClasses && (
                          <span className="event-day-classes-count">
                            ({clases.length} {clases.length === 1 ? 'clase' : 'clases'})
                          </span>
                        )}
                      </div>
                      {dia.titulo && (
                        <h3 className="event-day-title">{dia.titulo}</h3>
                      )}
                      {hasClasses && (
                        <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} event-day-toggle`}></i>
                      )}
                    </div>
                    {(dia.horaInicio || dia.horaFin) && (
                      <div className="event-day-time">
                        <i className="fas fa-clock"></i>
                        <span>
                          {dia.horaInicio && formatTime(dia.horaInicio)}
                          {dia.horaInicio && dia.horaFin && ' - '}
                          {dia.horaFin && formatTime(dia.horaFin)}
                        </span>
                      </div>
                    )}
                    {dia.descripcion && (
                      <p className="event-day-description">{dia.descripcion}</p>
                    )}
                    
                    {/* Clases del día */}
                    {isExpanded && hasClasses && (
                      <div className="event-day-classes">
                        <h4 className="event-day-classes-title">
                          <i className="fas fa-list"></i>
                          Clases del día
                        </h4>
                        <div className="event-day-classes-list">
                          {clases.map((clase, claseIndex) => (
                            <div key={clase.id || claseIndex} className="event-day-class-item">
                              <div className="event-day-class-header">
                                <span className="event-day-class-order">{claseIndex + 1}</span>
                                <h5 className="event-day-class-title">
                                  {clase.tituloclaseevento || clase.titulo || 'Clase sin título'}
                                </h5>
                              </div>
                              {clase.descripcionbreve && (
                                <p className="event-day-class-description">{clase.descripcionbreve}</p>
                              )}
                              {clase.descripcioncompleta && (
                                <div 
                                  className="event-day-class-full-description"
                                  dangerouslySetInnerHTML={{ __html: clase.descripcioncompleta }}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Location Section */}
      {event.formato === 'presencial' && (event.direccion || event.linkPresencial || event.linkpresencial) && (
        <section className="event-detail-location">
          <div className="event-detail-container">
            <h2 className="event-detail-section-title">
              <i className="fas fa-map-marker-alt me-2"></i>
              Ubicación
            </h2>
            <div className="event-detail-location-content">
              {event.direccion && (
                <div className="event-location-address">
                  <i className="fas fa-map-pin"></i>
                  <p>{event.direccion}</p>
                </div>
              )}
              
              {/* Google Maps Embed */}
              {(event.direccion || event.linkPresencial || event.linkpresencial) && (
                <div className="event-location-map">
                  <iframe
                    src={(() => {
                      const linkPresencial = event.linkPresencial || event.linkpresencial;
                      
                      // Si hay linkPresencial, intentar extraer coordenadas
                      if (linkPresencial) {
                        // Intentar extraer coordenadas de la URL de Google Maps (formato @lat,lng)
                        const coordMatch = linkPresencial.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
                        if (coordMatch) {
                          const lat = coordMatch[1];
                          const lng = coordMatch[2];
                          // Usar embed de Google Maps con coordenadas
                          return `https://www.google.com/maps?q=${lat},${lng}&output=embed`;
                        }
                        
                        // Si la URL contiene "place", extraer el lugar
                        const placeMatch = linkPresencial.match(/place\/([^/@]+)/);
                        if (placeMatch) {
                          const place = decodeURIComponent(placeMatch[1].replace(/\+/g, ' '));
                          return `https://www.google.com/maps?q=${encodeURIComponent(place)}&output=embed`;
                        }
                      }
                      
                      // Si hay dirección, usarla directamente
                      if (event.direccion) {
                        const encodedAddress = encodeURIComponent(event.direccion);
                        return `https://www.google.com/maps?q=${encodedAddress}&output=embed`;
                      }
                      
                      return '';
                    })()}
                    width="100%"
                    height="450"
                    style={{ border: 0, borderRadius: '0.5rem' }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Ubicación del evento"
                  />
                </div>
              )}
              
              {(event.linkPresencial || event.linkpresencial) && (
                <a
                  href={event.linkPresencial || event.linkpresencial}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="event-location-link"
                >
                  <i className="fas fa-external-link-alt"></i>
                  Ver en Google Maps
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Virtual Link Section */}
      {event.formato === 'virtual' && (event.linkVirtual || event.linkvirtual) && (
        <section className="event-detail-virtual">
          <div className="event-detail-container">
            <h2 className="event-detail-section-title">
              <i className="fas fa-video me-2"></i>
              Link Virtual
            </h2>
            <div className="event-detail-virtual-content">
              <a
                href={event.linkVirtual || event.linkvirtual}
                target="_blank"
                rel="noopener noreferrer"
                className="event-virtual-link"
              >
                <i className="fas fa-external-link-alt"></i>
                Acceder al evento virtual
              </a>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default EventsDetallePage;

